import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { pageTitle } from '../../helper';
import Cta from '../Cta';
import PageHeading from '../PageHeading';
import Div from '../Div';
import SectionHeading from '../SectionHeading';
import Spacing from '../Spacing';

const jobs = [
  {
    slug: 'net-developer-middle',
    title: '.NET Developer (Middle)',
    level: 'Middle',
    location: 'Dubai / Remote',
    employmentType: 'Full-time',
    salary: '12,000 – 18,000 AED / month',
    experience: '3+ years with C# / .NET Core',
    summary: 'Develop, maintain, and improve backend services and REST APIs for enterprise apps, collaborating with product and QA to ship scalable, secure solutions.',
    responsibilities: [
      'Develop and maintain .NET-based backend services and APIs',
      'Collaborate with cross-functional teams to define technical solutions',
      'Write clean, maintainable, well-documented code',
      'Participate in code reviews and sprint planning',
      'Troubleshoot and resolve application issues',
    ],
    requirements: [
      '3+ years of experience with .NET / C#',
      'Experience with REST APIs and microservices',
      'Knowledge of SQL and relational databases',
      'Understanding of the software development lifecycle',
      'Good communication skills in English',
    ],
    niceToHave: [
      'Experience with Docker and Kubernetes',
      'Background in fintech, payment, or high-load systems',
    ],
  },
  {
    slug: 'net-developer-senior',
    title: '.NET Developer (Senior)',
    level: 'Senior',
    location: 'Dubai / Hybrid',
    employmentType: 'Full-time',
    salary: '20,000 – 30,000 AED / month',
    experience: '5+ years professional .NET',
    summary: 'Lead the design and implementation of high-performance .NET backend systems, mentor developers, and drive architectural decisions for scalability and reliability.',
    responsibilities: [
      'Design and develop scalable backend architectures',
      'Lead code reviews and enforce best practices',
      'Optimize system performance and security',
      'Mentor junior and middle developers',
      'Collaborate with stakeholders on technical decisions',
    ],
    requirements: [
      '5+ years of experience with .NET / C#',
      'Strong experience with microservices architecture',
      'Advanced knowledge of databases and performance tuning',
      'Experience with cloud platforms is a plus',
      'Strong leadership and communication skills',
    ],
    niceToHave: [],
  },
  {
    slug: 'flutter-developer-senior',
    title: 'Flutter Developer (Senior)',
    level: 'Senior',
    location: 'Dubai / Remote',
    employmentType: 'Full-time',
    salary: '18,000 – 28,000 AED / month',
    experience: '4+ years mobile, 3+ years Flutter',
    summary: 'Build high-quality iOS/Android apps in Flutter, ensuring performance, security, and scalability while guiding mobile architecture and best practices.',
    responsibilities: [
      'Develop and maintain Flutter mobile applications',
      'Ensure UI/UX consistency across platforms',
      'Integrate APIs and third-party services',
      'Lead mobile architecture decisions',
      'Mentor junior developers',
    ],
    requirements: [
      '4+ years of mobile development experience',
      'Strong knowledge of Flutter and Dart',
      'Experience with REST APIs',
      'Understanding of app performance optimization',
      'Published apps on App Store or Google Play',
    ],
    niceToHave: [],
  },
  {
    slug: 'dba-postgresql-middle',
    title: 'Database Administrator – PostgreSQL (Middle)',
    level: 'Middle',
    location: 'Dubai / Remote',
    employmentType: 'Full-time',
    salary: '14,000 – 20,000 AED / month',
    experience: '3+ years PostgreSQL administration',
    summary: 'Manage, optimize, and maintain PostgreSQL databases to ensure high availability, performance, security, and reliable backups for production systems.',
    responsibilities: [
      'Manage PostgreSQL databases',
      'Monitor database performance and optimize queries',
      'Ensure data security and backups',
      'Support development teams with database design',
      'Handle database migrations',
    ],
    requirements: [
      '3+ years experience as a PostgreSQL DBA',
      'Strong SQL knowledge',
      'Experience with replication and backups',
      'Understanding of performance tuning',
      'Linux administration skills',
    ],
    niceToHave: [],
  },
  {
    slug: 'devops-engineer-middle',
    title: 'DevOps Engineer (Middle)',
    level: 'Middle',
    location: 'Dubai / Hybrid',
    employmentType: 'Full-time',
    salary: '15,000 – 22,000 AED / month',
    experience: '3+ years DevOps',
    summary: 'Own CI/CD pipelines, infrastructure automation, monitoring, and reliability, collaborating with development teams to improve deployments and uptime.',
    responsibilities: [
      'Manage CI/CD pipelines',
      'Automate infrastructure using IaC tools',
      'Monitor systems and ensure uptime',
      'Collaborate with development teams',
      'Improve deployment processes',
    ],
    requirements: [
      '3+ years DevOps experience',
      'Knowledge of Docker and Kubernetes',
      'Experience with cloud platforms',
      'CI/CD tools knowledge',
      'Scripting skills (Bash, Python)',
    ],
    niceToHave: [],
  },
  {
    slug: 'devops-engineer-junior',
    title: 'DevOps Engineer (Junior)',
    level: 'Junior',
    location: 'Dubai / Remote',
    employmentType: 'Full-time',
    salary: '7,000 – 11,000 AED / month',
    experience: '1+ year DevOps or systems',
    summary: 'Support CI/CD and deployments, monitor systems and logs, assist automation tasks, and learn modern DevOps practices under senior mentorship.',
    responsibilities: [
      'Assist in managing CI/CD pipelines',
      'Monitor systems and logs',
      'Support deployment processes',
      'Work with senior DevOps engineers',
      'Document processes',
    ],
    requirements: [
      'Basic knowledge of Linux',
      'Understanding of DevOps concepts',
      'Familiarity with Docker is a plus',
      'Willingness to learn',
      'Good problem-solving skills',
    ],
    niceToHave: [],
  },
  {
    slug: 'product-owner',
    title: 'Product Owner',
    level: 'Middle',
    location: 'Dubai / Hybrid',
    employmentType: 'Full-time',
    salary: '18,000 – 25,000 AED / month',
    experience: '3+ years Product Owner/PM',
    summary: 'Define product vision and backlog, translate business needs into clear requirements, and guide delivery of digital products in close collaboration with stakeholders.',
    responsibilities: [
      'Define and prioritize product backlog',
      'Work closely with stakeholders',
      'Translate business needs into requirements',
      'Support development teams',
      'Monitor product performance',
    ],
    requirements: [
      'Experience as a Product Owner or Business Analyst',
      'Strong understanding of Agile/Scrum',
      'Excellent communication skills',
      'Analytical mindset',
      'Experience in digital products',
    ],
    niceToHave: [],
  },
  {
    slug: 'ios-developer-middle',
    title: 'iOS Developer (Swift – Middle)',
    level: 'Middle',
    location: 'Dubai / Remote',
    employmentType: 'Full-time',
    salary: '14,000 – 20,000 AED / month',
    experience: '3+ years iOS',
    summary: 'Develop and maintain iOS apps in Swift, implement features, fix bugs, optimize performance, and deliver App Store–ready builds with strong API integrations.',
    responsibilities: [
      'Develop iOS applications using Swift',
      'Collaborate with design and backend teams',
      'Fix bugs and improve performance',
      'Implement new features',
      'Participate in code reviews',
    ],
    requirements: [
      '3+ years of iOS development experience',
      'Strong knowledge of Swift',
      'Experience with REST APIs',
      'Understanding of iOS design guidelines',
      'Experience with App Store deployment',
    ],
    niceToHave: [],
  },
];

export { jobs };

export default function CareersPage() {
  pageTitle('Careers');
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <PageHeading
        title="Careers"
        bgSrc="/images/contact_hero_bg.jpeg"
        pageLinkText="Careers"
      />
      <Spacing lg="150" md="80" />
      <Div className="container">
        <SectionHeading
          title="Open Positions"
          subtitle="Join Our Team"
        />
        <Spacing lg="30" md="20" />
        <p style={{ maxWidth: 700, color: '#999', lineHeight: 1.8 }}>
          We are always looking for talented individuals who are passionate about building excellent customer experiences.
          If you want to contribute to secure merchant services and customer support operations, TUR DER HOFFNUNG is the right place for you.
        </p>
        <Spacing lg="70" md="40" />

        <Div className="row">
          {jobs.map((job, index) => (
            <Div className="col-lg-6" key={index}>
              <Div
                style={{
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 15,
                  padding: '30px',
                  marginBottom: 30,
                }}
              >
                <h3 className="cs-font_22 cs-m0" style={{ marginBottom: 8 }}>
                  {job.title}
                </h3>
                <Div style={{ display: 'flex', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
                  <span
                    style={{
                      fontSize: 13,
                      padding: '4px 12px',
                      borderRadius: 20,
                      border: '1px solid rgba(255,255,255,0.15)',
                      color: '#ff4a17',
                    }}
                  >
                    {job.level}
                  </span>
                  <span
                    style={{
                      fontSize: 13,
                      padding: '4px 12px',
                      borderRadius: 20,
                      border: '1px solid rgba(255,255,255,0.15)',
                      color: '#999',
                    }}
                  >
                    {job.location}
                  </span>
                  <span
                    style={{
                      fontSize: 13,
                      padding: '4px 12px',
                      borderRadius: 20,
                      border: '1px solid rgba(255,255,255,0.15)',
                      color: '#999',
                    }}
                  >
                    {job.employmentType}
                  </span>
                </Div>
                <Div style={{ display: 'flex', gap: 20, marginBottom: 15, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 14, color: '#ccc' }}>
                    <strong className="cs-accent_color">Salary:</strong> {job.salary}
                  </span>
                  <span style={{ fontSize: 14, color: '#ccc' }}>
                    <strong className="cs-accent_color">Exp:</strong> {job.experience}
                  </span>
                </Div>
                <p className="cs-m0" style={{ color: '#999', lineHeight: 1.7, fontSize: 15, marginBottom: 20 }}>
                  {job.summary}
                </p>
                <Link
                  to={`/careers/${job.slug}`}
                  className="cs-text_btn"
                  style={{ fontSize: 15 }}
                >
                  <span>View Details</span>
                  <Icon icon="bi:arrow-right" />
                </Link>
              </Div>
            </Div>
          ))}
        </Div>

        <Spacing lg="50" md="30" />
        <Div style={{ textAlign: 'center' }}>
          <p style={{ color: '#999', fontSize: 16, lineHeight: 1.8 }}>
            Don't see a role that fits? Contact us through{' '}
            <a href="https://turderhoffnung.com/contact" className="cs-accent_color">
              turderhoffnung.com/contact
            </a>
          </p>
        </Div>
      </Div>
      <Spacing lg="150" md="80" />
      <Cta
        title="https://turderhoffnung.com"
        bgSrc="/images/cta_bg_2.jpeg"
        variant="rounded-0"
      />
    </>
  );
}
